import React, { FunctionComponent, ReactElement, useCallback, useState } from 'react';

import { Placement } from '@yandex-360/orb-components/dist/components/Popover2';
import { useSelector } from 'react-redux';

import MoreButtonPopover from '../../../../../../shared/components/more-button-popover';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { useDotColor } from '../../hooks/use-dot-color';
import { withParticipantsNotifications, WrappingButtonContainerProps } from '../participants-button';
import { ToolbarButton } from '../toolbar-button';
import styles from './styles.css';

type MoreButtonProps = WrappingButtonContainerProps & {
    visible?: boolean;
    disabled?: boolean;
    onClick: () => void;
    items: Array<ReactElement | undefined | null>;
    popupClassName?: string;
    isShowLimitationPopup: boolean;
    toggleLimitationPopup: () => void;
    placement?: Placement;
    showDots: boolean;
    hasOtherSharing?: boolean;
};

const MoreButton: FunctionComponent<MoreButtonProps> = ({
    visible,
    disabled,
    onClick,
    items,
    popupClassName,
    isShowLimitationPopup,
    toggleLimitationPopup,
    placement = 'top start',
    showDots,
    innerRef,
    onMouseEnter,
    onMouseLeave,
    hasOtherSharing = false,
    changeNotificationVisibility,
}) => {
    const buttonRef = React.useRef<HTMLDivElement>(null);

    const isTouch = useSelector((state) => state.ua?.isTouch);

    const [showMoreButtonPopover, setMoreButtonPopover] = useState(false);
    const dotColor = useDotColor({ showDots, hasOtherSharing });

    const toggleMoreButtonPopover = useCallback(() => {
        if (isShowLimitationPopup && !showMoreButtonPopover) {
            toggleLimitationPopup();
        }

        if (!isTouch) {
            changeNotificationVisibility?.(showMoreButtonPopover);
        }
        setMoreButtonPopover(!showMoreButtonPopover);
    }, [showMoreButtonPopover, toggleLimitationPopup, isShowLimitationPopup, changeNotificationVisibility, isTouch]);

    const wrappedOnClick = useCallback(() => {
        toggleMoreButtonPopover();
        onClick();
    }, [onClick, toggleMoreButtonPopover]);

    return visible ? (
        <div className={styles.container} ref={innerRef} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <MoreButtonPopover
                placement={placement as Placement}
                className={popupClassName}
                triggerRef={buttonRef}
                isOpen={showMoreButtonPopover}
                items={items}
                onClose={toggleMoreButtonPopover}
            />
            <div ref={buttonRef}>
                <ToolbarButton
                    onClick={wrappedOnClick}
                    iconName="more"
                    disabled={disabled}
                    title={i18n('toolbar', 'more-popup-alt-title')}
                    testId="more-popup-alt-button"
                    dotType={dotColor}
                />
            </div>
        </div>
    ) : null;
};

export { MoreButton };
export const MoreButtonWithNotifications = withParticipantsNotifications(MoreButton);
